import { Injectable } from '@angular/core';
import { 
  Auth,
  user,
  signInWithRedirect, 
  getRedirectResult, 
  signOut, 
  User, 
  onAuthStateChanged,
  GoogleAuthProvider,
  UserCredential
} from '@angular/fire/auth';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public user$: Observable<User | null>;
  private userSubscription: Subscription;
  private currentUserSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  private googleProvider = new GoogleAuthProvider();

  constructor(
    private auth: Auth,
    private router: Router
  ) {
    this.user$ = user(auth);

    // Subscribe to auth state changes
    this.userSubscription = this.user$.subscribe((user) => {
      this.currentUserSubject.next(user);
    });

    // Configure Google provider
    this.googleProvider.addScope('profile');
    this.googleProvider.addScope('email');
    
    // Handle redirect result on app initialization
    this.handleRedirectResult();
  }

  // Sign in with Google using redirect
  async signInWithGoogleRedirect(): Promise<void> {
    try {
      await signInWithRedirect(this.auth, this.googleProvider);
    } catch (error) {
      console.error('Error during Google sign-in redirect:', error);
      throw error;
    }
  }

  // Handle redirect result after returning from Google OAuth
  private async handleRedirectResult(): Promise<UserCredential | null> {
    try {
      const result = await getRedirectResult(this.auth);
      if (result) {
        console.log('User signed in successfully:', result.user);
        // Optionally navigate to a specific route after successful login
        // this.router.navigate(['/dashboard']);
        return result;
      }
      return null;
    } catch (error) {
      console.error('Error handling redirect result:', error);
      throw error;
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      await signOut(this.auth);
      // Optionally navigate to login page after sign out
      // this.router.navigate(['/login']);
      this.router.navigate(['/welcome']);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.getCurrentUser() !== null;
  }

  // Get user ID
  getUserId(): string | null {
    const user = this.getCurrentUser();
    return user ? user.uid : null;
  }

  // Get user email
  getUserEmail(): string | null {
    const user = this.getCurrentUser();
    return user ? user.email : null;
  }

  // Get user display name
  getUserDisplayName(): string | null {
    const user = this.getCurrentUser();
    return user ? user.displayName : null;
  }

  // Get user photo URL
  getUserPhotoURL(): string | null {
    const user = this.getCurrentUser();
    return user ? user.photoURL : null;
  }

  // Check redirect result manually (useful for components)
  async checkRedirectResult(): Promise<UserCredential | null> {
    return await this.handleRedirectResult();
  }
}